import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ClipLoader } from "react-spinners";
import MobileNav from "../../components/mobileNav/MobileNav";
import {
  fetchPermissions,
  updateUserPermission,
} from "../../reduxSlices/permissionsSlice";
import {
  fetchUsers,
  fetchUserPermissions,
  setSelectedUser,
} from "../../reduxSlices/userSlice";
import "./permesions.css";
import Swal from "sweetalert2";

const Permesions = () => {
  const dispatch = useDispatch();
  const {
    availablePermissions: permissions,
    loading: permissionsLoading,
    error: permissionsError,
  } = useSelector((state) => state.permissions);

  const {
    list: users,
    selectedUser,
    userPermissions,
    loading: usersLoading,
    loadingPermissions: loadingUserPermissions,
    error: usersError,
    permissionsError: userPermissionsError,
  } = useSelector((state) => state.users);

  // Permission labels mapping
  const permissionLabels = {
    CanManagePendingRequests: "إدارة الطلبات الجديده",
    CanWritePendingRequests: "تعديل الطلبات الجديده",
    CanManageWaitingRequests: "إدارة قائمه الانتظار ",
    CanWriteWaitingRequests: "تعديل قائمه الانتظار ",
    CanManageProgressRequests: "إدارة طلبات قيد التنفيذ ",
    CanWriteProgressRequests: "تعديل طلبات قيد التنفيذ ",
    CanManageRejectedRequests: "إدارة الطلبات المرفوضة",
    CanManageCancelRequests: "إدارة الطلبات الملغاة",
    CanManageFinishedRequests: "إدارة الطلبات المنتهية",
    CanManageReports: "إدارة التقارير",
    CanWriteReports: "تعديل التقارير",
    CanManagAccountsManagment: "إدارة الحسابات",
    CanManagUploadImages: "إدارة الصور",
    CanManagHomeControl: "إدارة الصفحه الرئيسيه",
    CanManagReserLock: "إدارة صفحه الحجز",
    CanManagTeamControl: "إدارة قسم الفريق",
    CanManagSmsControl: "تعديل الرسائل",
    CanManagPermesions: "إدارة الصلاحيات ",
    CanManagSettings: "إدارة الاعدادات",
    CanManagaddAcTypes: "إدارة انواع المكيفات",
    CanManagaddAccount: "إضافه الحسابات",
    CanManagaddSpareParts: "إدارة قطع الغيار",
  };

  // Normalize permission value to boolean
  const normalizePermission = (value) => {
    if (typeof value === "string") {
      return value.toLowerCase() === "true";
    }
    return Boolean(value);
  };

  // Group permissions by category
  const groupPermissions = (permissions) => {
    if (!permissions?.length) return {};

    return permissions.reduce((groups, permission) => {
      const category = permission.Key.includes("Reports")
        ? "التقارير"
        : permission.Key.includes("Requests")
        ? "الطلبات"
        : "أخرى";

      groups[category] = groups[category] || [];
      groups[category].push(permission);
      return groups;
    }, {});
  };

  // Fetch initial data
  useEffect(() => {
    dispatch(fetchPermissions());
    dispatch(fetchUsers());
  }, [dispatch]);

  // Fetch permissions when user is selected
  useEffect(() => {
    if (!selectedUser || !permissions) return;
    dispatch(fetchUserPermissions(selectedUser.Id));
  }, [selectedUser, permissions, dispatch]);

  // Handle permission toggle
  const handlePermissionToggle = (permissionKey, isChecked) => {
    if (!selectedUser) return;

    const newValue = isChecked ? "true" : "false";

    // Update in backend
    dispatch(
      updateUserPermission({
        Type: permissionKey,
        Value: newValue,
        UserId: selectedUser.Id,
      })
    ).then(() => {
      // Refetch permissions after update to ensure consistency
      dispatch(fetchUserPermissions(selectedUser.Id));
    });
  };

  // Handle user selection
  const handleSelectUser = (user) => {
    dispatch(setSelectedUser(user));
  };

  // Loading state
  if (permissionsLoading || usersLoading) {
    return (
      <div className="loading-container">
        <ClipLoader color="#00b1ff" size={50} />
      </div>
    );
  }

  // Error state
  if (permissionsError || usersError || userPermissionsError) {
    return (
      <div className="permesions">
        <div className="permesions-container">
          <h2>حدث خطأ أثناء جلب البيانات</h2>
          <p>{permissionsError || usersError || userPermissionsError}</p>
        </div>
      </div>
    );
  }

  // Empty state
  if (!permissions?.length || !users?.length) {
    return (
      <div className="permesions">
        <div className="permesions-container">
          <h2>لا توجد بيانات متاحة</h2>
        </div>
      </div>
    );
  }

  const groupedPermissions = groupPermissions(permissions);

  return (
    <>
      <MobileNav />
      <div className="permesions">
        <div className="permesions-container">
          <h2>إدارة صلاحيات المستخدمين</h2>

          <div className="user-permissions-grid">
            {/* Users List */}
            <div className="users-list">
              <h3>المستخدمين</h3>
              <div className="users-scrollable">
                {users.map((user) => (
                  <div
                    key={user.Id}
                    className={`user-item ${
                      selectedUser?.Id === user.Id ? "selected" : ""
                    }`}
                    onClick={() => handleSelectUser(user)}
                  >
                    <div className="user-name">
                      {user.UserName}
                      {selectedUser?.Id === user.Id && (
                        <span className="selected-badge">✓</span>
                      )}
                    </div>
                    {selectedUser?.Id === user.Id && loadingUserPermissions && (
                      <ClipLoader color="#00b1ff" size={15} />
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Permissions Panel */}
            <div className="permissions-panel">
              {selectedUser ? (
                <>
                  <div className="selected-user-header">
                    <h3>صلاحيات المستخدم: {selectedUser.UserName}</h3>
                  </div>

                  {loadingUserPermissions ? (
                    <div className="loading-permissions">
                      <ClipLoader color="#00b1ff" size={30} />
                      <p>جاري تحميل الصلاحيات...</p>
                    </div>
                  ) : (
                    Object.entries(groupedPermissions).map(
                      ([category, perms]) => (
                        <div key={category} className="permission-category">
                          <h4 className="category-title">{category}</h4>
                          <div className="permissions-grid">
                            {perms.map((permission) => {
                              const isActive = normalizePermission(
                                userPermissions[permission.Key]
                              );
                              return (
                                <label
                                  onClick={(e) => {
                                    const isSuperAdmin =
                                      localStorage.getItem("IsSuperAdmin") ===
                                      "true";
                                    if (!isSuperAdmin) {
                                      e.preventDefault(); // Prevent toggling or interaction
                                      Swal.fire({
                                        title: "لا تملك الصلاحية",
                                        text: "ليس لديك إذن لتعديل هذه الصلاحيات",
                                        icon: "error",
                                        confirmButtonText: "حسناً",
                                        confirmButtonColor: "#3085d6",
                                      });
                                    }
                                  }}
                                  key={permission.Key}
                                  className={`permission-item ${
                                    isActive ? "active" : "inactive"
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={isActive}
                                    onChange={(e) => {
                                      const isSuperAdmin =
                                        localStorage.getItem("IsSuperAdmin") ===
                                        "true";
                                      if (!isSuperAdmin) {
                                        e.preventDefault();
                                        return;
                                      }
                                      handlePermissionToggle(
                                        permission.Key,
                                        e.target.checked
                                      );
                                    }}
                                    className="permission-checkbox"
                                  />
                                  <div className="permission-info">
                                    <div className="permission-name">
                                      {permissionLabels[permission.Key] ||
                                        permission.Key}
                                    </div>
                                    <div className="permission-status">
                                      {isActive ? "مفعلة" : "غير مفعلة"}
                                    </div>
                                  </div>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      )
                    )
                  )}
                </>
              ) : (
                <div className="select-user-prompt">
                  <p>يرجى اختيار مستخدم لإدارة صلاحياته</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Permesions;
